import { batch, createEffect, createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { AUTHORED_SCOPES, type AuthoredScope, type NewBoardForm } from '../lib/dashboard-writes';
import { Field } from './Field';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Create-a-board dialog (#1033). Presenter — the route owns the POST and the
// refetch. Three fields, because the create contract has exactly three moving
// parts: a name, who the board is for, and its first widget. The widget is a
// markdown note (see `noteBoardBody`): the SPA renders widget specs, it does not
// design them, so data widgets stay API/MCP-authored.

export type CreateDashboardModalProps = {
  readonly open: boolean;
  /** True while the POST is in flight — disables the submit. */
  readonly pending: boolean;
  /** The API's reason for refusing (name collides with a built-in, personal
   * scope without a user session), shown inline. */
  readonly error?: string | null;
  readonly onCreate: (form: NewBoardForm) => void;
  readonly onClose: () => void;
};

export function CreateDashboardModal(props: CreateDashboardModalProps): JSX.Element {
  const [name, setName] = createSignal('');
  const [scope, setScope] = createSignal<AuthoredScope>('org');
  const [note, setNote] = createSignal('');

  // Re-arm on every OPEN, never on unmount: the dialog's exit animation delays
  // that past a fast reopen (same rule as `<EnrollTransportToken/>`), so without this the
  // last board's name is still in the field — and re-submitting it is a refusal
  // (the list mirror, then the API's 409) over the board just made.
  createEffect(() => {
    if (!props.open) return;
    batch(() => {
      setName('');
      setScope('org');
      setNote('');
    });
  });

  const valid = (): boolean => name().trim().length > 0 && note().trim().length > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onCreate({ name: name(), scope: scope(), note: note() });
  };

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('dashboards.create_title')}
      desc={t('dashboards.create_desc')}
      onClose={props.onClose}
    >
      <form class="create-dashboard__form" onSubmit={submit}>
        <div class="field-group">
          <Field label={t('dashboards.form_name')} id="create-dashboard-name">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                maxLength={200}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder={t('dashboards.form_name_placeholder')}
                required
              />
            )}
          </Field>
          <Field
            label={t('dashboards.form_scope')}
            id="create-dashboard-scope"
            desc={t('dashboards.form_scope_desc')}
          >
            {(a11y) => (
              <Select<AuthoredScope>
                a11y={a11y}
                label={t('dashboards.form_scope')}
                block
                value={scope()}
                // The options ARE `AUTHORED_SCOPES`, so the only values this
                // select can hand back are that array's members.
                options={AUTHORED_SCOPES.map((s) => ({
                  value: s,
                  label: t(`dashboards.scope_${s}`),
                }))}
                onChange={setScope}
              />
            )}
          </Field>
          <Field
            label={t('dashboards.form_note')}
            id="create-dashboard-note"
            desc={t('dashboards.form_note_desc')}
          >
            {(a11y) => (
              <textarea
                {...a11y}
                class="textarea"
                rows={4}
                maxLength={20000}
                value={note()}
                onInput={(e) => setNote(e.currentTarget.value)}
                placeholder={t('dashboards.form_note_placeholder')}
                required
              />
            )}
          </Field>
        </div>

        {props.error ? (
          <p class="field__error" role="alert">
            {props.error}
          </p>
        ) : null}

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('dashboards.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
            {t(props.pending ? 'dashboards.creating' : 'dashboards.create_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
